export type AuthAudienceId = 'blo' | 'plantation' | 'lgu' | 'project_developer';
export type AuthPlantationCrop = 'banana' | 'coconut';

export interface AuthUseCase {
  icon: string;
  title: string;
  help: string;
}

export interface AuthPandoyCopy {
  line: string;
  question: string;
}

export interface AuthAudienceShowcase {
  id: AuthAudienceId;
  tabLabel: string;
  roleHint: string;
  headline: string;
  tagline: string;
  stories: AuthUseCase[];
  storiesByCrop?: Record<AuthPlantationCrop, AuthUseCase[]>;
  pandoy: AuthPandoyCopy;
}

/** Maps signup account type → showcase audience tab. */
export function audienceFromSignupRole(role: string): AuthAudienceId {
  if (role === 'lgu' || role === 'municipal') return 'lgu';
  if (role === 'plantation_owner' || role === 'banana_plantation') return 'plantation';
  if (role === 'project_developer') return 'project_developer';
  return 'blo';
}

export function signupRoleFromAudience(id: AuthAudienceId): string {
  if (id === 'plantation') return 'plantation_owner';
  if (id === 'lgu') return 'lgu';
  if (id === 'project_developer') return 'project_developer';
  return 'blo';
}

export const AUTH_AUDIENCE_SHOWCASES: AuthAudienceShowcase[] = [
  {
    id: 'blo',
    tabLabel: 'BLO',
    roleHint: 'For rice big land owners (BLO)',
    headline: 'Every field mapped. Every block managed.',
    tagline: 'Digitize 100% of your farms. Monitor and manage by block or farmer group.',
    stories: [
      {
        icon: 'ri-leaf-line',
        title: 'Your whole farm, in one place',
        help: 'Track crop health and field activities across every parcel and farmer group.',
      },
      {
        icon: 'ri-cloudy-line',
        title: 'Farm alerts and priorities',
        help: 'Know which fields need action on crop health, weather, pests, or water.',
      },
      {
        icon: 'ri-seedling-line',
        title: 'Plan for a better harvest',
        help: 'Estimate yields and coordinate irrigation, inputs, and harvest schedules.',
      },
    ],
    pandoy: {
      line: 'Understand your maps and get guidance on what to do next.',
      question: 'Which fields should my team prioritize today?',
    },
  },
  {
    id: 'plantation',
    tabLabel: 'Plantations',
    roleHint: 'For banana and coconut plantations',
    headline: 'Protect your crop. Reduce costly losses.',
    tagline: 'Digitize 100% of your plantation. Monitor and manage every block or group.',
    stories: [],
    storiesByCrop: {
      banana: [
        {
          icon: 'ri-layout-grid-line',
          title: 'Every block connected',
          help: 'Bring satellite maps, drone imagery, and field records into one view.',
        },
        {
          icon: 'ri-flight-takeoff-line',
          title: 'Combat pests and diseases',
          help: 'Track Sigatoka risk and prioritize scouting and treatment by block.',
        },
        {
          icon: 'ri-contrast-drop-2-line',
          title: 'Protect millions in crop value',
          help: 'Target interventions to reduce avoidable losses and unnecessary costs.',
        },
      ],
      coconut: [
        {
          icon: 'ri-layout-grid-line',
          title: 'Every block connected',
          help: 'Bring satellite maps, drone imagery, and field records into one view.',
        },
        {
          icon: 'ri-bug-line',
          title: 'Combat pests and diseases',
          help: 'Track pest risk and prioritize scouting and treatment by block.',
        },
        {
          icon: 'ri-contrast-drop-2-line',
          title: 'Protect millions in crop value',
          help: 'Target interventions to reduce avoidable losses and unnecessary costs.',
        },
      ],
    },
    pandoy: {
      line: 'Understand your maps and get guidance on what to do next.',
      question: 'Which banana blocks need Sigatoka checks first?',
    },
  },
  {
    id: 'lgu',
    tabLabel: 'LGU',
    roleHint: 'For municipal and provincial agriculture offices',
    headline: 'All farms in view. Agri reports made easy.',
    tagline: 'Digitize 100% of farms in your coverage area. Manage by barangay or group.',
    stories: [
      {
        icon: 'ri-plant-line',
        title: 'Farm and farmer-group maps',
        help: 'Monitor crops, planted areas, and expected yields by barangay or association.',
      },
      {
        icon: 'ri-alarm-warning-line',
        title: 'Alerts and response priorities',
        help: 'Identify areas needing field checks, assistance, or early intervention.',
      },
      {
        icon: 'ri-slideshow-line',
        title: 'Regular agricultural reports',
        help: 'Prepare crop, risk, and farm-status reports for your LGU and DA.',
      },
    ],
    pandoy: {
      line: 'Understand your maps and get guidance on what to do next.',
      question: "Prepare this month's agricultural report for my area.",
    },
  },
  {
    id: 'project_developer',
    tabLabel: 'Carbon',
    roleHint: 'For carbon project developers',
    headline: 'Plan a project area. Keep the evidence.',
    tagline: 'Screen restoration and low-carbon rice areas. Farms are enrolled inside the project later.',
    stories: [
      {
        icon: 'ri-leaf-line',
        title: 'Screen a project area',
        help: 'Review a restoration or rice envelope before any farm is linked.',
      },
      {
        icon: 'ri-file-text-line',
        title: 'Keep the evidence',
        help: 'Save the boundary, notes, and gaps with the project. A file is not a verified credit.',
      },
      {
        icon: 'ri-map-pin-line',
        title: 'Separate the pathways',
        help: 'Restoration and irrigated rice stay distinct, including their hectares and seasons.',
      },
    ],
    pandoy: {
      line: 'Ask what is still unknown before any carbon claim.',
      question: 'What should I check on this project area?',
    },
  },
];

export function showcaseForAudience(id: AuthAudienceId): AuthAudienceShowcase {
  return AUTH_AUDIENCE_SHOWCASES.find((s) => s.id === id) ?? AUTH_AUDIENCE_SHOWCASES[0];
}

export function storiesForShowcase(
  showcase: AuthAudienceShowcase,
  crop: AuthPlantationCrop = 'banana'
): AuthUseCase[] {
  if (showcase.storiesByCrop) return showcase.storiesByCrop[crop];
  return showcase.stories;
}

/** Pandoy example prompt for plantation tab, crop-aware. */
export function pandoyQuestionForPlantation(crop: AuthPlantationCrop): string {
  if (crop === 'coconut') {
    return 'Which coconut blocks need scouting first this week?';
  }
  return showcaseForAudience('plantation').pandoy.question;
}
